// frontend/src/pages/VerifyEmailPage.tsx

import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { config } from '@/lib/config'
import { useAuth } from '@/components/providers/auth-provider'
import { useMutation } from '@tanstack/react-query'
import { users } from '@/lib/api'

export function VerifyEmailPage() {
  const location = useLocation()
  const navigate = useNavigate()

  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'code_input'>(
    'loading'
  )
  const [message, setMessage] = useState<string>('Verifying your email...')
  const [code, setCode] = useState<string>('')
  const [verifying, setVerifying] = useState(false)

  // ✅ ADD: Get checkAuth function to refresh user state
  const { user, isAuthenticated, isLoading, checkAuth } = useAuth()

  const resendEmailMutation = useMutation({
    mutationFn: users.resendVerificationEmail,
    onSuccess: () => {
      setMessage('Verification email sent! Check your inbox.')
    },
    onError: (error: any) => {
      setMessage(error.message || 'Failed to resend email. Please try again.')
    },
  })

  // 🔗 Handle token from URL (link-based verification)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const token = params.get('token')

    // No token in URL → show code input mode
    if (!token) {
      setStatus('code_input')
      setMessage('Enter the 6-digit code from your email')
      return
    }

    const verify = async () => {
      try {
        const res = await axios.get(
          `${config.apiUrl}/api/v1/auth/verify-email`,
          {
            params: { token },
            withCredentials: true,  // ✅ CRITICAL: Include cookies
          }
        )
        setStatus('success')
        setMessage(res.data?.message || 'Email verified successfully!')

        // ✅ ADDED: Refresh auth state to get updated user
        await checkAuth()

        // ✅ ADDED: Auto-redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard', { replace: true })
        }, 2000)

      } catch (err: any) {
        // Link failed - show code input as fallback
        setStatus('code_input')
        const detail =
          err?.response?.data?.detail ||
          'Verification link failed or expired.'
        setMessage(`${detail} Please enter your 6-digit code below:`)
      }
    }

    setStatus('loading')
    setMessage('Verifying your email...')
    verify()
  }, [location.search, checkAuth, navigate])

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (code.length !== 6) {
      setMessage('Please enter a 6-digit code')
      return
    }

    // For code-based verification we DO require a logged-in user
    if (isLoading) {
      setMessage('Checking your session, please wait…')
      return
    }
    if (!isAuthenticated || !user) {
      setMessage('Please log in before verifying with a code.')
      return
    }

    setVerifying(true)

    try {
      const res = await axios.post(
        `${config.apiUrl}/api/v1/auth/verify-email-code`,
        {
          user_id: user.id,
          code: code.trim(),
        },
        {
          withCredentials: true,  // ✅ CRITICAL: Include cookies
        }
      )

      setStatus('success')
      setMessage(res.data?.message || 'Email verified successfully!')

      // ✅ ADDED: Refresh auth state
      await checkAuth()

      // ✅ ADDED: Auto-redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard', { replace: true })
      }, 2000)

    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        'Invalid or expired code. Please try again.'
      setMessage(detail)
    } finally {
      setVerifying(false)
    }
  }

  const handleResendEmail = () => {
    // Resend requires auth (uses current_user on backend)
    if (!isAuthenticated) {
      setMessage('Log in first, then you can resend the verification email.')
      return
    }
    resendEmailMutation.mutate()
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-50 p-4">
      <div className="max-w-md w-full bg-slate-900/70 border border-slate-700 rounded-2xl p-8">
        {/* Loading State */}
        {status === 'loading' && (
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-sm text-slate-300">{message}</p>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-16 w-16 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold mb-2">Email verified 🎉</h1>
            <p className="text-sm text-slate-300 mb-4">{message}</p>
            {/* ✅ UPDATED: Show redirect message */}
            <p className="text-sm text-slate-400 mb-6">Redirecting to dashboard...</p>
            <button
              className="w-full px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-sm font-medium transition-colors"
              onClick={() => navigate('/dashboard', { replace: true })}
            >
              Continue to Dashboard
            </button>
          </div>
        )}

        {/* Code Input State - UNCHANGED */}
        {status === 'code_input' && (
          <div>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-semibold mb-2">Verify Your Email</h1>
              <p className="text-sm text-slate-300">{message}</p>
            </div>

            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="code"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  Verification Code
                </label>
                <input
                  id="code"
                  type="text"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, ''))
                  }
                  placeholder="000000"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg 
                           text-slate-50 placeholder-slate-500 text-center text-2xl tracking-[0.5em] 
                           font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={verifying}
                  autoFocus
                />
                <p className="mt-2 text-xs text-slate-400">
                  Enter the 6-digit code from your email
                </p>
                {!isAuthenticated && !isLoading && (
                  <p className="mt-1 text-xs text-amber-400">
                    You'll need to log in to verify using a code.
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={verifying || code.length !== 6}
                className="w-full px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 
                         text-sm font-medium transition-colors disabled:bg-slate-700 
                         disabled:cursor-not-allowed disabled:text-slate-500"
              >
                {verifying ? (
                  <span className="flex items-center justify-center">
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Verifying...
                  </span>
                ) : (
                  'Verify Email'
                )}
              </button>
            </form>

            {/* Help Section */}
            <div className="mt-6 pt-6 border-t border-slate-700 text-center">
              <p className="text-sm text-slate-400 mb-3">
                Didn&apos;t receive the email?
              </p>
              <button
                onClick={handleResendEmail}
                disabled={resendEmailMutation.isPending}
                className="text-sm text-blue-400 hover:text-blue-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendEmailMutation.isPending
                  ? 'Sending...'
                  : 'Resend verification email'}
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-16 w-16 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold mb-2 text-red-400">
              Verification issue
            </h1>
            <p className="text-sm text-slate-300 mb-6">{message}</p>
            <button
              className="px-4 py-2 rounded-md bg-slate-700 hover:bg-slate-600 text-sm font-medium"
              onClick={() => setStatus('code_input')}
            >
              Try entering code instead
            </button>
          </div>
        )}
      </div>
    </div>
  )
}